import { type InputHTMLAttributes } from 'react';
import { useTranslation } from 'react-i18next';

/**
 * Sprint 18 Block F T18.28 — Custel DS PhoneInput.
 *
 * Country dropdown + numeric input. TR varsayılan (+90, 10 hane).
 * Pilot expansion için TR/KKTC/UK/DE/US ülke kodları (Faz 2'de genişler).
 *
 * Format: dropdown'da +90, input'ta 10 hane (5XX XXX XX XX UI mask opsiyonel).
 * Value full E.164 olarak parent'a döner (örn. "+905551234567").
 */

interface Country {
  iso: string;
  flag: string;
  code: string; // +90
  label: string;
  /** Local numara uzunluğu (TR: 10, US: 10, UK: 10, DE: 11) */
  localLength: number;
}

const COUNTRIES: Country[] = [
  { iso: 'TR', flag: '🇹🇷', code: '+90', label: 'Türkiye', localLength: 10 },
  { iso: 'CY', flag: '🇨🇾', code: '+392', label: 'KKTC', localLength: 7 },
  { iso: 'DE', flag: '🇩🇪', code: '+49', label: 'Almanya', localLength: 11 },
  { iso: 'GB', flag: '🇬🇧', code: '+44', label: 'İngiltere', localLength: 10 },
  { iso: 'US', flag: '🇺🇸', code: '+1', label: 'ABD', localLength: 10 },
];

/**
 * E.164 değeri seçili ülkenin TAM yerel uzunluğuna ulaştı mı (submit-validity).
 * `phone.length >= 10` tarzı zayıf kontrollerin yerine kullanılır — TR için
 * "+90" + 10 hane = 13 karakter; ülkeye göre değişir. Bilinmeyen prefix → false.
 */
export function isCompletePhoneE164(value: string): boolean {
  const country = COUNTRIES.find((c) => value.startsWith(c.code));
  if (!country) return false;
  const local = value.slice(country.code.length);
  return new RegExp(`^\\d{${country.localLength}}$`).test(local);
}

/** Kayıtlı E.164 değerinden ülke ISO'su türet (edit formu init'i) — eşleşmezse TR. */
export function countryIsoFromE164(value: string | null | undefined): string {
  if (!value) return 'TR';
  // En uzun prefix önce (+392 vs +39 karışmasın diye code uzunluğuna göre sırala).
  const match = [...COUNTRIES].sort((a, b) => b.code.length - a.code.length).find((c) => value.startsWith(c.code));
  return match?.iso ?? 'TR';
}

export interface CustelPhoneInputProps {
  /** Tam E.164 (örn. "+905551234567"). Boş string = yeni input. */
  value: string;
  onChange: (e164: string) => void;
  /** Seçili ülke ISO (default TR). */
  countryIso?: string;
  onCountryChange?: (iso: string) => void;
  /** Hata stateı (kırmızı border). */
  error?: boolean;
  disabled?: boolean;
  placeholder?: string;
  inputProps?: Omit<InputHTMLAttributes<HTMLInputElement>, 'onChange' | 'value' | 'type' | 'inputMode'>;
}

function parseE164(value: string, country: Country): string {
  // Strip prefix → return local digits
  const stripped = value.replace(/[^\d+]/g, '');
  if (stripped.startsWith(country.code)) {
    return stripped.slice(country.code.length);
  }
  if (stripped.startsWith('+')) {
    // Different country code; strip just the +
    return stripped.slice(1);
  }
  return stripped;
}

export function CustelPhoneInput({
  value,
  onChange,
  countryIso = 'TR',
  onCountryChange,
  error = false,
  disabled = false,
  placeholder,
  inputProps,
}: CustelPhoneInputProps) {
  const { t } = useTranslation('components');
  const country = COUNTRIES.find((c) => c.iso === countryIso) ?? COUNTRIES[0];
  const local = parseE164(value, country);
  const finalPlaceholder = placeholder ?? (country.iso === 'TR' ? '5XX XXX XX XX' : t('phone.placeholder'));

  return (
    <div
      className={`flex overflow-hidden rounded border ${
        error ? 'border-custel-danger' : 'border-custel-border focus-within:border-custel-text'
      } ${disabled ? 'bg-custel-bg-subtle' : 'bg-custel-surface'}`}
    >
      <select
        value={countryIso}
        disabled={disabled}
        onChange={(e) => {
          const next = COUNTRIES.find((c) => c.iso === e.target.value) ?? COUNTRIES[0];
          onCountryChange?.(next.iso);
          // E.164'i yeni prefix ile yeniden inşa et
          onChange(`${next.code}${local}`);
        }}
        className="border-r border-custel-border bg-custel-bg-subtle px-2 py-2 text-sm font-mono focus:outline-none disabled:cursor-not-allowed"
        aria-label={t('phone.countryCode')}
      >
        {COUNTRIES.map((c) => (
          <option key={c.iso} value={c.iso}>
            {c.flag} {c.code}
          </option>
        ))}
      </select>
      <input
        {...inputProps}
        type="tel"
        inputMode="numeric"
        autoComplete="tel-national"
        value={local}
        disabled={disabled}
        maxLength={country.localLength + 4} // tire/boşluk için pay
        placeholder={finalPlaceholder}
        onChange={(e) => {
          const digits = e.target.value.replace(/\D/g, '').slice(0, country.localLength);
          onChange(`${country.code}${digits}`);
        }}
        className="flex-1 px-3 py-2 text-sm font-mono focus:outline-none disabled:cursor-not-allowed"
        aria-label={t('phone.number')}
      />
    </div>
  );
}
